import React, { useState } from 'react';
import {
  Check,
  Crown,
  Dices,
  KeyRound,
  RefreshCw,
  ShieldAlert,
  Shuffle,
  Users,
  VolumeX,
  X,
} from 'lucide-react';
import type { ClientGameView, GameIntent, PublicCardView } from '#shared';

// ============================================================================
// 1. GRANT LIBRARY ACCESS MODAL (Matches Reference Screenshot)
// ============================================================================

interface GrantLibraryAccessModalProps {
  view: ClientGameView;
  onDispatch: (intent: GameIntent) => void;
  onClose: () => void;
}

export function GrantLibraryAccessModal({ view, onDispatch, onClose }: GrantLibraryAccessModalProps) {
  const mySeatId = view.viewerSeatId;
  const mySeat = mySeatId ? view.seats[mySeatId] : null;
  const opponents = view.activeSeatOrder
    .filter((sid) => sid !== mySeatId)
    .map((sid) => view.seats[sid])
    .filter((s) => Boolean(s && s.publicId && s.status !== 'eliminated'));

  const [granteeSeatId, setGranteeSeatId] = useState<string>(opponents[0]?.seatId ?? '');
  const [scope, setScope] = useState<'top' | 'entire'>('top');
  const [count, setCount] = useState<number>(1);
  const [takeFaceDown, setTakeFaceDown] = useState<boolean>(false);

  const libraryCount = mySeat?.zones.libraryCount ?? 0;

  return (
    <div className="fixed inset-0 z-[105] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl bg-[#0b1120] border border-indigo-500/40 p-5 space-y-4 shadow-2xl text-xs text-slate-100">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-base font-extrabold flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-emerald-400" />
              <span>Grant library access</span>
            </h2>
            <p className="text-slate-400 mt-0.5">
              Let a player look at cards and take them into exile.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-slate-800 text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Player Dropdown */}
        <div className="space-y-1.5">
          <label className="block font-bold text-slate-200">Player</label>
          <select
            value={granteeSeatId}
            disabled={opponents.length === 0}
            onChange={(e) => setGranteeSeatId(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-slate-100 disabled:text-slate-500"
          >
            {opponents.length === 0 ? (
              <option value="">No players available</option>
            ) : (
              opponents.map((opp) => (
                <option key={opp.seatId} value={opp.seatId}>
                  {opp.displayName} ({opp.seatId.replace('_', ' ')})
                </option>
              ))
            )}
          </select>
          {opponents.length === 0 && (
            <p className="text-[11px] text-slate-500">No other active players are seated at the table.</p>
          )}
        </div>

        {/* Cards they can access */}
        <div className="space-y-2">
          <label className="block font-bold text-slate-200">Cards they can access</label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setScope('top')}
              className={`py-2.5 rounded-xl font-bold border transition-colors ${
                scope === 'top'
                  ? 'bg-emerald-950/60 border-emerald-400 text-emerald-100'
                  : 'bg-slate-950 border-slate-800 text-slate-300 hover:border-slate-700'
              }`}
            >
              Top cards
            </button>
            <button
              type="button"
              onClick={() => setScope('entire')}
              className={`py-2.5 rounded-xl font-bold border transition-colors ${
                scope === 'entire'
                  ? 'bg-emerald-950/60 border-emerald-400 text-emerald-100'
                  : 'bg-slate-950 border-slate-800 text-slate-300 hover:border-slate-700'
              }`}
            >
              Entire library
            </button>
          </div>
        </div>

        {scope === 'top' && (
          <div className="flex items-center gap-3">
            <span className="text-slate-300 font-semibold w-14 leading-tight">How many</span>
            <input
              type="number"
              min={1}
              max={Math.max(1, libraryCount)}
              value={count}
              onChange={(e) =>
                setCount(Math.max(1, Math.min(Math.max(1, libraryCount), Number(e.target.value) || 1)))
              }
              className="flex-1 px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 font-bold text-white"
            />
            <span className="text-slate-400 w-10 text-right">of {libraryCount}</span>
          </div>
        )}

        {/* Take face down toggle */}
        <label className="flex items-center justify-between gap-3 p-3.5 rounded-xl bg-slate-950/90 border border-slate-800 cursor-pointer">
          <div>
            <div className="font-bold text-slate-100">Take face down</div>
            <div className="text-[11px] text-slate-400 mt-0.5">
              Only that player sees the cards they take. Everyone sees the action.
            </div>
          </div>
          <input
            type="checkbox"
            checked={takeFaceDown}
            onChange={(e) => setTakeFaceDown(e.target.checked)}
            className="w-4 h-4 accent-emerald-400 shrink-0"
          />
        </label>

        <p className="text-[11px] text-slate-400 leading-relaxed">
          You control the order and shuffle. Access ends when they finish or your library changes.
        </p>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 font-bold text-slate-200"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!granteeSeatId || libraryCount === 0}
            onClick={() => {
              onDispatch({
                type: 'GRANT_LIBRARY_ACCESS',
                granteeSeatId,
                scope,
                count,
                takeFaceDown,
              });
              onClose();
            }}
            className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 font-extrabold text-white"
          >
            Grant access
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// 2. GRANTED LIBRARY VIEWER MODAL (Shown to Grantee Only)
// ============================================================================

interface GrantedLibraryViewerModalProps {
  grantView: ClientGameView['grantedLibraryViews'][number];
  onDispatch: (intent: GameIntent) => void;
  onInspect: (card: PublicCardView) => void;
}

export function GrantedLibraryViewerModal({
  grantView,
  onDispatch,
  onInspect,
}: GrantedLibraryViewerModalProps) {
  const [search, setSearch] = useState('');
  const filtered = grantView.cards.filter((c) =>
    !search.trim()
      ? true
      : (c.card?.name ?? '').toLowerCase().includes(search.toLowerCase()) ||
        (c.card?.typeLine ?? '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-[105] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-2xl max-h-[85vh] rounded-2xl bg-[#0b1120] border border-emerald-500/50 p-5 flex flex-col gap-3 shadow-2xl text-xs text-slate-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-extrabold text-emerald-300 flex items-center gap-2">
              <KeyRound className="w-4 h-4" />
              <span>
                {grantView.ownerName}&apos;s Library ({grantView.scope === 'entire' ? 'Entire Library' : `Top ${grantView.cards.length}`})
              </span>
            </h2>
            <p className="text-[11px] text-slate-400">
              {grantView.takeFaceDown
                ? 'Face-down mode ON: cards you take remain secret even from their owner.'
                : 'Face-up mode: cards you take are visible at the table.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onDispatch({ type: 'REVOKE_LIBRARY_ACCESS', ownerSeatId: grantView.ownerSeatId })}
            className="px-3 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold"
          >
            Done
          </button>
        </div>

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search card name or type..."
          className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white"
        />

        <div className="flex-1 overflow-y-auto space-y-1.5 pr-1">
          {filtered.map((item, idx) => (
            <div
              key={item.instanceId}
              className="p-2 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between gap-2"
            >
              <button
                type="button"
                onClick={() => onInspect(item)}
                className="text-left min-w-0 flex-1 hover:text-amber-300"
              >
                <div className="font-bold truncate">
                  #{idx + 1} · {item.card?.name ?? 'Card'}
                </div>
                <div className="text-[10px] text-slate-400 truncate">{item.card?.typeLine}</div>
              </button>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() =>
                    onDispatch({
                      type: 'TAKE_FROM_GRANTED_LIBRARY',
                      ownerSeatId: grantView.ownerSeatId,
                      instanceId: item.instanceId,
                      destination: 'exile',
                    })
                  }
                  className="px-2.5 py-1 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-bold text-[11px]"
                >
                  To Exile
                </button>
                <button
                  type="button"
                  onClick={() =>
                    onDispatch({
                      type: 'TAKE_FROM_GRANTED_LIBRARY',
                      ownerSeatId: grantView.ownerSeatId,
                      instanceId: item.instanceId,
                      destination: 'battlefield',
                    })
                  }
                  className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-[11px]"
                >
                  Battlefield
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// 3. PREGAME RULE 0, MAGICAL CHAIRS, TEAMS (2HG) & 1–8 POD CONTROLS MODAL
// ============================================================================

interface PregameRuleZeroAndPodModalProps {
  view: ClientGameView;
  onDispatch: (intent: GameIntent) => void;
  onClose: () => void;
}

export function PregameRuleZeroAndPodModal({
  view,
  onDispatch,
  onClose,
}: PregameRuleZeroAndPodModalProps) {
  const [tab, setTab] = useState<'pregame' | 'teams_pod'>('pregame');
  const rz = view.pregame.ruleZero;
  const mySid = view.viewerSeatId;
  const iAgreed = Boolean(mySid && rz.agreedSeatIds.includes(mySid));
  const canAdmin = view.isHost || view.isJudge;

  const moveSeatInOrder = (seatId: string, dir: -1 | 1) => {
    const arr = [...view.seatOrder];
    const idx = arr.indexOf(seatId);
    const targetIdx = idx + dir;
    if (idx === -1 || targetIdx < 0 || targetIdx >= arr.length) return;
    const tmp = arr[idx];
    arr[idx] = arr[targetIdx];
    arr[targetIdx] = tmp;
    onDispatch({ type: 'REORDER_SEATS', newSeatOrder: arr });
  };

  return (
    <div className="fixed inset-0 z-[105] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-2xl max-h-[88vh] rounded-2xl bg-[#0b1120] border border-slate-700 p-5 flex flex-col gap-4 shadow-2xl text-xs text-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-amber-400" />
            <h2 className="text-base font-extrabold">Pod, Teams &amp; Rule 0 Ceremony</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-slate-800 text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 p-1 rounded-xl bg-slate-950 border border-slate-800">
          <button
            type="button"
            onClick={() => setTab('pregame')}
            className={`py-1.5 rounded-lg font-bold ${
              tab === 'pregame' ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            Rule 0 &amp; Pregame Seating
          </button>
          <button
            type="button"
            onClick={() => setTab('teams_pod')}
            className={`py-1.5 rounded-lg font-bold ${
              tab === 'teams_pod' ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            1–8 Pod, Teams (2HG) &amp; Turns
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {tab === 'pregame' ? (
            <>
              {/* Magical Chairs / High-Roll Seat Determination */}
              <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 space-y-2.5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-extrabold text-slate-100">Pregame Seat Order &amp; First Player</div>
                    <div className="text-[11px] text-slate-400">
                      Current mode: <span className="text-amber-300 font-semibold">{view.pregame.seatingMode}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => onDispatch({ type: 'RUN_PREGAME_SEATING', mode: 'magical_chairs' })}
                      className="px-2.5 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold flex items-center gap-1"
                    >
                      <Shuffle className="w-3.5 h-3.5" />
                      <span>Magical Chairs</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => onDispatch({ type: 'RUN_PREGAME_SEATING', mode: 'high_roll' })}
                      className="px-2.5 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold flex items-center gap-1"
                    >
                      <Dices className="w-3.5 h-3.5" />
                      <span>High-Roll d20</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => onDispatch({ type: 'REVEAL_COMMANDERS_PREGAME' })}
                      className="px-2.5 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-100 font-bold flex items-center gap-1"
                    >
                      <Crown className="w-3.5 h-3.5 text-amber-400" />
                      <span>Reveal Commanders</span>
                    </button>
                  </div>
                </div>

                {Object.keys(view.pregame.highRollResults).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {view.seatOrder.map((sid) => {
                      const roll = view.pregame.highRollResults[sid];
                      if (!roll) return null;
                      return (
                        <span
                          key={sid}
                          className={`px-2 py-0.5 rounded-full border text-[11px] font-bold ${
                            view.activeSeatId === sid
                              ? 'bg-amber-500/20 border-amber-400 text-amber-200'
                              : 'bg-slate-900 border-slate-700 text-slate-300'
                          }`}
                        >
                          {view.seats[sid]?.displayName}: d20 = {roll}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Rule 0 Checklist */}
              <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-extrabold text-slate-100">Rule 0 Pod Checklist</span>
                  <div className="flex items-center gap-1">
                    <span className="text-[11px] text-slate-400 mr-1">Target Bracket:</span>
                    {([1, 2, 3, 4, 5] as const).map((b) => (
                      <button
                        key={b}
                        type="button"
                        onClick={() =>
                          onDispatch({
                            type: 'UPDATE_RULE_ZERO',
                            patch: { targetBracket: rz.targetBracket === b ? null : b },
                          })
                        }
                        className={`px-2 py-0.5 rounded font-bold border ${
                          rz.targetBracket === b
                            ? 'bg-amber-500 border-amber-300 text-slate-950'
                            : 'bg-slate-900 border-slate-700 text-slate-300'
                        }`}
                      >
                        B{b}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {(
                    [
                      ['proxiesAllowed', 'Playtest / Proxies OK'],
                      ['fastMana', 'Fast Mana (Crypt/Mox)'],
                      ['infiniteCombos', '2-Card Infinite Combos'],
                      ['massLandDenial', 'Mass Land Denial'],
                      ['stax', 'Hard Stax Locks'],
                    ] as const
                  ).map(([key, label]) => (
                    <label
                      key={key}
                      className="flex items-center justify-between gap-2 p-2 rounded-lg bg-slate-900 border border-slate-800 cursor-pointer"
                    >
                      <span className="text-[11px] text-slate-200">{label}</span>
                      <input
                        type="checkbox"
                        checked={rz[key]}
                        onChange={(e) =>
                          onDispatch({
                            type: 'UPDATE_RULE_ZERO',
                            patch: { [key]: e.target.checked },
                          })
                        }
                        className="accent-violet-500"
                      />
                    </label>
                  ))}
                </div>

                <input
                  value={rz.houseRulesNote}
                  onChange={(e) =>
                    onDispatch({
                      type: 'UPDATE_RULE_ZERO',
                      patch: { houseRulesNote: e.target.value },
                    })
                  }
                  placeholder="House rules note (e.g. No Sol Ring Turn 1, infinite scry shortcut OK)..."
                  className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-800 text-white"
                />

                <div className="flex items-center justify-between pt-1">
                  <div className="flex flex-wrap gap-1.5">
                    {view.seatOrder
                      .filter((sid) => view.seats[sid]?.publicId)
                      .map((sid) => {
                        const agreed = rz.agreedSeatIds.includes(sid);
                        return (
                          <span
                            key={sid}
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                              agreed
                                ? 'bg-emerald-500/20 border-emerald-400 text-emerald-200'
                                : 'bg-slate-900 border-slate-800 text-slate-400'
                            }`}
                          >
                            {agreed ? '✓ ' : '• '}
                            {view.seats[sid]?.displayName}
                          </span>
                        );
                      })}
                  </div>
                  {mySid && (
                    <button
                      type="button"
                      onClick={() => onDispatch({ type: 'AGREE_RULE_ZERO', agreed: !iAgreed })}
                      className={`px-3 py-1.5 rounded-xl font-extrabold flex items-center gap-1 ${
                        iAgreed
                          ? 'bg-emerald-500 text-slate-950'
                          : 'bg-violet-600 hover:bg-violet-500 text-white'
                      }`}
                    >
                      <Check className="w-3.5 h-3.5" />
                      <span>{iAgreed ? 'Rule 0 Agreed' : 'Agree to Rule 0'}</span>
                    </button>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Teams & Simultaneous Turn Toggles */}
              <div className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 space-y-2.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-extrabold text-slate-100">Teams &amp; Two-Headed Giant Options</span>
                  {canAdmin && (
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={view.settings.teamMode}
                          onChange={(e) =>
                            onDispatch({
                              type: 'UPDATE_TABLE_SETTINGS',
                              patch: {
                                teamMode: e.target.checked,
                                sharedTeamLife: e.target.checked,
                                simultaneousTeamTurns: e.target.checked,
                                teammatesSeeHands: e.target.checked,
                              },
                            })
                          }
                          className="accent-violet-500"
                        />
                        <span>Team Mode (2HG)</span>
                      </label>
                      {view.settings.teamMode && (
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={view.settings.simultaneousTeamTurns}
                            onChange={(e) =>
                              onDispatch({
                                type: 'UPDATE_TABLE_SETTINGS',
                                patch: { simultaneousTeamTurns: e.target.checked },
                              })
                            }
                            className="accent-violet-500"
                          />
                          <span>Simultaneous Turns</span>
                        </label>
                      )}
                    </div>
                  )}
                </div>

                {/* Seat Table: Reorder, Team Selector, Extra/Skip Turn, Away Vote, Flag Mute */}
                <div className="space-y-1.5">
                  {view.seatOrder.map((sid, idx) => {
                    const s = view.seats[sid];
                    if (!s) return null;
                    const isAway = !s.connected || s.status === 'afk' || s.status === 'brb';
                    return (
                      <div
                        key={sid}
                        className="p-2 rounded-xl bg-slate-900 border border-slate-800 flex flex-wrap items-center justify-between gap-2"
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className="w-2.5 h-2.5 rounded-full"
                            style={{ backgroundColor: s.profileColor }}
                          />
                          <span className="font-bold">
                            #{idx + 1} {s.displayName}
                          </span>
                          {s.status === 'eliminated' && (
                            <span className="px-1.5 py-0.5 rounded bg-rose-950 text-rose-300 text-[10px]">
                              Eliminated
                            </span>
                          )}
                          {isAway && s.publicId && (
                            <span className="px-1.5 py-0.5 rounded bg-amber-950 text-amber-300 text-[10px]">
                              Away
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-1.5">
                          {view.settings.teamMode && (canAdmin || sid === mySid) && (
                            <select
                              value={s.teamId ?? ''}
                              onChange={(e) =>
                                onDispatch({
                                  type: 'ASSIGN_TEAM',
                                  targetSeatId: sid,
                                  teamId: e.target.value || null,
                                })
                              }
                              className="px-2 py-1 rounded bg-slate-950 border border-slate-700 text-[11px]"
                            >
                              <option value="">No Team</option>
                              <option value="team_1">Team 1</option>
                              <option value="team_2">Team 2</option>
                              <option value="team_3">Team 3</option>
                              <option value="team_4">Team 4</option>
                            </select>
                          )}

                          {s.publicId && s.status !== 'eliminated' && (
                            <>
                              <button
                                type="button"
                                onClick={() => onDispatch({ type: 'ADD_EXTRA_TURN', targetSeatId: sid })}
                                className="px-2 py-1 rounded bg-indigo-600/30 hover:bg-indigo-600/50 border border-indigo-400/50 text-indigo-200 text-[10px] font-bold"
                              >
                                +Extra Turn
                              </button>
                              <button
                                type="button"
                                onClick={() => onDispatch({ type: 'SKIP_NEXT_TURN', targetSeatId: sid })}
                                className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 text-[10px] font-bold"
                              >
                                Skip Next
                              </button>
                            </>
                          )}

                          {isAway && s.publicId && sid !== mySid && s.status !== 'eliminated' && (
                            <>
                              <button
                                type="button"
                                onClick={() =>
                                  onDispatch({ type: 'VOTE_AWAY_PLAYER', targetSeatId: sid, action: 'skip_turn' })
                                }
                                className="px-2 py-1 rounded bg-amber-500/20 border border-amber-400/60 text-amber-200 text-[10px] font-bold"
                              >
                                Vote Skip
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  onDispatch({ type: 'VOTE_AWAY_PLAYER', targetSeatId: sid, action: 'remove' })
                                }
                                className="px-2 py-1 rounded bg-rose-500/20 border border-rose-400/60 text-rose-200 text-[10px] font-bold flex items-center gap-0.5"
                              >
                                <ShieldAlert className="w-3 h-3" />
                                <span>Vote Remove</span>
                              </button>
                            </>
                          )}

                          {canAdmin && s.publicId && (
                            <button
                              type="button"
                              onClick={() =>
                                onDispatch({ type: 'SET_FLAG_MUTE', targetSeatId: sid, muted: !s.flagMuted })
                              }
                              title="Mute/Unmute Flag Spam"
                              className={`p-1 rounded border ${
                                s.flagMuted
                                  ? 'bg-rose-600 border-rose-400 text-white'
                                  : 'bg-slate-950 border-slate-700 text-slate-400 hover:text-white'
                              }`}
                            >
                              <VolumeX className="w-3 h-3" />
                            </button>
                          )}

                          {canAdmin && (
                            <div className="flex items-center gap-0.5">
                              <button
                                type="button"
                                onClick={() => moveSeatInOrder(sid, -1)}
                                className="px-1.5 py-0.5 rounded bg-slate-950 border border-slate-700 text-[10px]"
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                onClick={() => moveSeatInOrder(sid, 1)}
                                className="px-1.5 py-0.5 rounded bg-slate-950 border border-slate-700 text-[10px]"
                              >
                                ↓
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer: Rematch Pod + Done */}
        <div className="flex items-center justify-between pt-2 border-t border-slate-800">
          {canAdmin ? (
            <button
              type="button"
              onClick={() => {
                onDispatch({ type: 'REMATCH_POD', reshuffleSeats: true });
                onClose();
              }}
              className="px-3.5 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold flex items-center gap-1.5"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Rematch Pod (Shuffle Seats &amp; Deal)</span>
            </button>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 font-bold text-white"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
